import { BehaviorSubject, combineLatest, filter, switchMap, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DesktopClientsTableComponent } from '@clients/components/desktop-clients-table/desktop-clients-table.component';
import { MobileClientsTableComponent } from '@clients/components/mobile-clients-table/mobile-clients-table.component';
import { IClientWithProperties } from '@clients/models/firebase/client.model';
import { ClientsService } from '@clients/services/clients/clients.service';
import { LayoutService } from '@layout/services/layout/layout.service';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { PageTitleComponent } from '@shared/components/sections/page-title/page-title.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';

@Component({
	selector: 'app-clients-dashboard',
	imports: [
		CommonModule,
		UnauthorisedBannerComponent,
		PageTitleComponent,
		DesktopClientsTableComponent,
		MobileClientsTableComponent,
		FormsModule,
		ReactiveFormsModule,
		LoadingStateComponent,
	],
	templateUrl: './clients-dashboard.component.html',
	styleUrl: './clients-dashboard.component.scss',
})
export class ClientsDashboardComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();
	private clientsService = inject(ClientsService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private layoutService = inject(LayoutService);

	selectedOrganisation: IOrganisationAndOrganisationUser | null;

	isLoading = true;
	clientsLoading = true;
	isMobileSize = true;
	userHasAccess = false;

	clients: Array<IClientWithProperties> = [];
	searchTerm$ = new BehaviorSubject<string>('');

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		combineLatest([this.organisationSelectedService.selectedOrganisation, this.searchTerm$])
			.pipe(
				takeUntil(this.destroyed$),
				filter(([selectedOrganisation]) => !!selectedOrganisation),
				switchMap(([selectedOrganisation, searchTerm]) => {
					this.selectedOrganisation = selectedOrganisation;
					if (
						selectedOrganisation &&
						[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
							selectedOrganisation.organisationUser.accessLevel,
						)
					) {
						this.userHasAccess = true;
						return this.clientsService.getClientsWithPropertiesByOrganisation(
							selectedOrganisation.organisationUser.organisation,
							searchTerm,
						);
					} else {
						this.userHasAccess = false;
						this.isLoading = false;
						return [];
					}
				}),
			)
			.subscribe((clients) => {
				this.clients = clients;
				this.isLoading = false;
				this.clientsLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	onSearchChange(value: string): void {
		this.clientsLoading = true;

		this.searchTerm$.next(value.trim().toLowerCase());
	}
}
